export const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_56a6a268-304a-44e7-9070-6b61950dd949/artifacts/3en7vtep_talkandgo_logo.png";

// Brand-aligned light green pill for initials avatars — same as the active
// "Tasks" sidebar item (emerald-100 bg with emerald-700 text).
const AVATAR_BG = "#dcfce7";  // emerald-100
const AVATAR_FG = "#047857";  // emerald-700

function _hashCode(s = "") {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
}

export function initialsFor(name = "?") {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || "")
    .join("") || "?";
}

/**
 * Returns a data-URL SVG showing the user's two-letter initials on the
 * brand green background. Drop-in replacement for previous photo URLs.
 */
export function avatarFor(name = "?") {
  const init = initialsFor(name);
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'>` +
    `<rect width='100%' height='100%' rx='60' fill='${AVATAR_BG}'/>` +
    `<text x='50%' y='50%' dy='.35em' font-family='-apple-system,Segoe UI,Roboto,sans-serif' ` +
    `font-size='52' font-weight='700' fill='${AVATAR_FG}' text-anchor='middle'>${init}</text>` +
    `</svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

/** Quietly silences the linter — kept in case future code needs a hash. */
export { _hashCode as _avatarHash };

export function formatHM(minutes = 0) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}

export function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export function formatTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}

/** Local-date YYYY-MM-DD (avoids UTC drift that toISOString causes). */
export function localISODate(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Parse YYYY-MM-DD into a LOCAL Date (avoids `new Date("YYYY-MM-DD")` UTC parsing). */
export function parseLocalDate(iso) {
  if (!iso) return new Date();
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Returns "Sun May 10" given an ISO date string. */
export function weekdayShort(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, (m || 1) - 1, d || 1);
  if (isNaN(dt.getTime())) return "";
  return `${WEEKDAYS[dt.getDay()]} ${MONTHS[dt.getMonth()]} ${d}`;
}

/** Returns "Sun, May 10, 2026" given an ISO date string. */
export function weekdayShortYear(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, (m || 1) - 1, d || 1);
  if (isNaN(dt.getTime())) return "";
  return `${WEEKDAYS[dt.getDay()]}, ${MONTHS[dt.getMonth()]} ${d}, ${y}`;
}

export function weekdayOnly(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, (m || 1) - 1, d || 1);
  if (isNaN(dt.getTime())) return "";
  return WEEKDAYS[dt.getDay()];
}
